"use client"

import { motion } from "framer-motion"
import { Home, Shield, Ruler, Paintbrush, MapPin, Handshake } from "lucide-react"
import { Card, CardContent } from "@/components/ui/card"

export function FeaturesSection() {
  return (
    <section id="services" className="px-6 py-24">
      <div className="max-w-5xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12"
        >
          <p className="text-sm font-medium text-brand uppercase tracking-wider mb-4">خدماتنا</p>
          <h2 className="font-display text-3xl md:text-4xl font-bold text-zinc-100 mb-4">
            لماذا تختار بنايه للعقارات؟
          </h2>
          <p className="text-zinc-500 max-w-xl mx-auto text-balance">
            نقدم لكم تجربة عقارية متكاملة من البحث حتى التسليم بأعلى معايير الجودة
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <Card className="group h-full overflow-hidden border-zinc-800/50 bg-zinc-900/50 hover:border-brand/30 transition-all duration-300 rounded-2xl">
              <CardContent className="p-6">
                <motion.div
                  className="w-12 h-12 rounded-xl bg-brand/30 flex items-center justify-center mb-4"
                  whileHover={{ rotate: [0, -10, 10, 0] }}
                  transition={{ duration: 0.5 }}
                >
                  <Home className="w-6 h-6 text-brand group-hover:text-brand-light transition-colors" />
                </motion.div>
                <h3 className="font-heading font-semibold text-zinc-100 text-lg mb-2">تصميمات عصرية</h3>
                <p className="text-zinc-500 text-sm">
                  وحدات سكنية بتصميمات معمارية حديثة تجمع بين الأناقة والراحة مع مراعاة أدق التفاصيل
                </p>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <Card className="group h-full overflow-hidden border-zinc-800/50 bg-zinc-900/50 hover:border-brand/30 transition-all duration-300 rounded-2xl">
              <CardContent className="p-6">
                <motion.div
                  className="w-12 h-12 rounded-xl bg-emerald-900/30 flex items-center justify-center mb-4"
                  whileHover={{ scale: 1.1 }}
                >
                  <Shield className="w-6 h-6 text-emerald-400 group-hover:text-emerald-300 transition-colors" />
                </motion.div>
                <h3 className="font-heading font-semibold text-zinc-100 text-lg mb-2">ضمان الجودة</h3>
                <p className="text-zinc-500 text-sm">
                  نستخدم أجود مواد البناء والتشطيب مع ضمان شامل على جميع الأعمال الإنشائية
                </p>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <Card className="group h-full overflow-hidden border-zinc-800/50 bg-zinc-900/50 hover:border-brand/30 transition-all duration-300 rounded-2xl">
              <CardContent className="p-6">
                <motion.div
                  className="w-12 h-12 rounded-xl bg-blue-900/30 flex items-center justify-center mb-4"
                  whileHover={{ y: -2 }}
                >
                  <MapPin className="w-6 h-6 text-blue-400 group-hover:text-blue-300 transition-colors" />
                </motion.div>
                <h3 className="font-heading font-semibold text-zinc-100 text-lg mb-2">مواقع استراتيجية</h3>
                <p className="text-zinc-500 text-sm">
                  مشاريعنا في مواقع متميزة قريبة من الخدمات والطرق الرئيسية لضمان أعلى عائد استثماري
                </p>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            <Card className="group h-full overflow-hidden border-zinc-800/50 bg-zinc-900/50 hover:border-brand/30 transition-all duration-300 rounded-2xl">
              <CardContent className="p-6">
                <motion.div
                  className="w-12 h-12 rounded-xl bg-purple-900/30 flex items-center justify-center mb-4"
                  whileHover={{ rotate: 180 }}
                  transition={{ duration: 0.4 }}
                >
                  <Paintbrush className="w-6 h-6 text-purple-400 group-hover:text-purple-300 transition-colors" />
                </motion.div>
                <h3 className="font-heading font-semibold text-zinc-100 text-lg mb-2">تشطيبات فاخرة</h3>
                <p className="text-zinc-500 text-sm">
                  تشطيبات عالية الجودة باستخدام أفضل الخامات المحلية والمستوردة لمنزل أحلامك
                </p>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.5 }}
          >
            <Card className="group h-full overflow-hidden border-zinc-800/50 bg-zinc-900/50 hover:border-brand/30 transition-all duration-300 rounded-2xl">
              <CardContent className="p-6">
                <motion.div
                  className="w-12 h-12 rounded-xl bg-rose-900/30 flex items-center justify-center mb-4"
                  whileHover={{ scale: 1.1 }}
                >
                  <Ruler className="w-6 h-6 text-rose-400 group-hover:text-rose-300 transition-colors" />
                </motion.div>
                <h3 className="font-heading font-semibold text-zinc-100 text-lg mb-2">مساحات متنوعة</h3>
                <p className="text-zinc-500 text-sm">
                  وحدات بمساحات متنوعة تناسب جميع الاحتياجات من الاستوديوهات وحتى الفيلات الكبيرة
                </p>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.6 }}
          >
            <Card className="group h-full overflow-hidden border-zinc-800/50 bg-zinc-900/50 hover:border-brand/30 transition-all duration-300 rounded-2xl">
              <CardContent className="p-6">
                <motion.div
                  className="w-12 h-12 rounded-xl bg-teal-900/30 flex items-center justify-center mb-4"
                  whileHover={{ y: -2 }}
                >
                  <Handshake className="w-6 h-6 text-teal-400 group-hover:text-teal-300 transition-colors" />
                </motion.div>
                <h3 className="font-heading font-semibold text-zinc-100 text-lg mb-2">أنظمة سداد مرنة</h3>
                <p className="text-zinc-500 text-sm">
                  خطط تقسيط مريحة وأنظمة سداد متعددة تناسب مختلف الميزانيات بدون فوائد
                </p>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </div>
    </section>
  )
}
